import type { z } from "zod";
import type { FormCategoriesListQueryDTO } from "@/modules/forms/domain/contracts/form-categories";
import type {
  CreateFieldBodySchema,
  CreateFormBodySchema,
  FormsListQueryDTO,
  PatchFormStatusBodySchema,
  ReorderFieldsBodySchema,
  UpdateFieldBodySchema,
} from "@/modules/forms/domain/contracts/forms";
import type { FormResponsesApi } from "@/modules/forms/presentation/api/form-responses-api";
import type { OffsetPageDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher, handleError } from "@/shared/lib/fetcher";
import type {
  FormCategoryDTO,
  FormDetailDTO,
  FormDTO,
  FormFieldDTO,
  FormLotteryDetailDTO,
  FormLotteryDrawResultDTO,
  FormLotteryEntriesDTO,
  FormLotteryPrizeDTO,
  FormLotteryPrizeInputDTO,
  FormLotterySettingsDTO,
  FormListItemDTO,
  FormResponsesListDTO,
  FormResponsesPrintDataDTO,
  FormResponsesSummaryDTO,
  PatchFormBodyDTO,
} from "./types";

type CreateFormBodyDTO = z.infer<typeof CreateFormBodySchema>;
type CreateFieldBodyDTO = z.infer<typeof CreateFieldBodySchema>;
type UpdateFieldBodyDTO = z.infer<typeof UpdateFieldBodySchema>;
type PatchFormStatusDTO = z.infer<typeof PatchFormStatusBodySchema>;
type ReorderFieldsDTO = z.infer<typeof ReorderFieldsBodySchema>;

export const formsApi = {
  async list(
    query: FormsListQueryDTO,
  ): Promise<OffsetPageDTO<FormListItemDTO>> {
    const url = new URL(`${config.apiUrl}/forms`);
    url.searchParams.set("limit", String(query.limit));
    url.searchParams.set("offset", String(query.offset));
    if (query.status) url.searchParams.set("status", query.status);
    if (query.categoryId) url.searchParams.set("categoryId", query.categoryId);
    return fetcher.get<OffsetPageDTO<FormListItemDTO>>(url.toString());
  },

  async get(id: string): Promise<FormDetailDTO> {
    return fetcher.get<FormDetailDTO>(`${config.apiUrl}/forms/${id}`);
  },

  async create(body: CreateFormBodyDTO): Promise<FormDetailDTO> {
    return fetcher.post<FormDetailDTO>(`${config.apiUrl}/forms`, body);
  },

  async patch(id: string, body: PatchFormBodyDTO): Promise<{ form: FormDTO }> {
    return fetcher.patch<{ form: FormDTO }>(
      `${config.apiUrl}/forms/${id}`,
      body,
    );
  },

  async patchStatus(
    id: string,
    body: PatchFormStatusDTO,
  ): Promise<{ form: FormDTO }> {
    return fetcher.patch<{ form: FormDTO }>(
      `${config.apiUrl}/forms/${id}/status`,
      body,
    );
  },

  async remove(id: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(`${config.apiUrl}/forms/${id}`);
  },

  async addField(
    formId: string,
    body: CreateFieldBodyDTO,
  ): Promise<{ field: FormFieldDTO }> {
    return fetcher.post<{ field: FormFieldDTO }>(
      `${config.apiUrl}/forms/${formId}/fields`,
      body,
    );
  },

  async updateField(
    formId: string,
    fieldId: string,
    body: UpdateFieldBodyDTO,
  ): Promise<{ field: FormFieldDTO }> {
    return fetcher.patch<{ field: FormFieldDTO }>(
      `${config.apiUrl}/forms/${formId}/fields/${fieldId}`,
      body,
    );
  },

  async removeField(formId: string, fieldId: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${config.apiUrl}/forms/${formId}/fields/${fieldId}`,
    );
  },

  async reorderFields(formId: string, body: ReorderFieldsDTO): Promise<void> {
    await fetcher.patch<{ fields: unknown }>(
      `${config.apiUrl}/forms/${formId}/fields/reorder`,
      body,
    );
  },

  async listResponses(
    formId: string,
    args: { limit: number; offset: number; filt?: string },
  ): Promise<FormResponsesListDTO> {
    const url = new URL(`${config.apiUrl}/forms/${formId}/responses`);
    url.searchParams.set("limit", String(args.limit));
    url.searchParams.set("offset", String(args.offset));
    if (args.filt?.trim()) url.searchParams.set("filt", args.filt.trim());
    return fetcher.get<FormResponsesListDTO>(url.toString());
  },

  async getResponsesSummary(formId: string): Promise<FormResponsesSummaryDTO> {
    return fetcher.get<FormResponsesSummaryDTO>(
      `${config.apiUrl}/forms/${formId}/responses/summary`,
    );
  },

  async fetchResponsesPrintData(
    formId: string,
    options?: { filt?: string; columnFieldIds?: string[] },
  ): Promise<FormResponsesPrintDataDTO> {
    const url = new URL(
      `${config.apiUrl}/forms/${formId}/responses/print-data`,
    );
    if (options?.filt?.trim())
      url.searchParams.set("filt", options.filt.trim());
    if (options?.columnFieldIds?.length) {
      url.searchParams.set("columns", options.columnFieldIds.join(","));
    }
    return fetcher.get<FormResponsesPrintDataDTO>(url.toString());
  },

  async downloadResponsesCsv(
    formId: string,
    options?: { filt?: string },
  ): Promise<void> {
    const url = new URL(
      `${config.apiUrl}/forms/${formId}/responses/export.csv`,
    );
    if (options?.filt?.trim())
      url.searchParams.set("filt", options.filt.trim());
    const res = await fetch(url.toString(), { credentials: "include" });
    if (!res.ok) return handleError(res);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = options?.filt?.trim()
      ? `form-${formId}-responses-filtered.csv`
      : `form-${formId}-responses.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  },

  async getAnswerFileReadUrl(
    formId: string,
    key: string,
  ): Promise<{ url: string }> {
    return fetcher.post<{ url: string }>(
      `${config.apiUrl}/forms/${formId}/responses/file-read-url`,
      { key },
    );
  },

  async deleteResponse(formId: string, responseId: string): Promise<void> {
    await fetcher.delete<{ ok: boolean }>(
      `${config.apiUrl}/forms/${formId}/responses/${responseId}`,
    );
  },

  async presignAdminResponseFile(
    formId: string,
    body: {
      fieldId: string;
      filename: string;
      contentType?: string;
      sizeBytes?: number;
    },
  ): Promise<{ uploadUrl: string; key: string }> {
    return fetcher.post<{ uploadUrl: string; key: string }>(
      `${config.apiUrl}/forms/${formId}/responses/presign`,
      body,
    );
  },

  async submitAdminResponse(
    formId: string,
    body: {
      answers: Array<{ fieldId: string; value?: string; fileKey?: string }>;
    },
  ): Promise<{ ok: true; responseId: string }> {
    const res = await fetch(`${config.apiUrl}/forms/${formId}/responses`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let code = "UNKNOWN";
      let fieldId: string | undefined;
      try {
        const raw: unknown = await res.json();
        if (raw !== null && typeof raw === "object") {
          if (
            "error" in raw &&
            typeof (raw as { error: unknown }).error === "string"
          ) {
            code = (raw as { error: string }).error;
          }
          const fid = (raw as { fieldId?: unknown }).fieldId;
          if (typeof fid === "string") fieldId = fid;
        }
      } catch {
        /* omit */
      }
      const err = new Error(code) as Error & {
        code: string;
        fieldId?: string;
      };
      err.code = code;
      err.fieldId = fieldId;
      throw err;
    }
    return (await res.json()) as { ok: true; responseId: string };
  },

  async getLottery(formId: string) {
    return fetcher.get<FormLotteryDetailDTO>(
      `${config.apiUrl}/forms/${formId}/lottery`,
    );
  },

  async saveLotterySettings(formId: string, body: FormLotterySettingsDTO) {
    return fetcher.put<{ settings: FormLotterySettingsDTO }>(
      `${config.apiUrl}/forms/${formId}/lottery/settings`,
      body,
    );
  },

  async createLotteryPrize(formId: string, body: FormLotteryPrizeInputDTO) {
    return fetcher.post<{ prize: FormLotteryPrizeDTO }>(
      `${config.apiUrl}/forms/${formId}/lottery/prizes`,
      body,
    );
  },

  async patchLotteryPrize(
    formId: string,
    prizeId: string,
    body: Partial<FormLotteryPrizeInputDTO>,
  ) {
    return fetcher.patch<{ prize: FormLotteryPrizeDTO }>(
      `${config.apiUrl}/forms/${formId}/lottery/prizes/${prizeId}`,
      body,
    );
  },

  async removeLotteryPrize(formId: string, prizeId: string) {
    return fetcher.delete<{ ok: boolean }>(
      `${config.apiUrl}/forms/${formId}/lottery/prizes/${prizeId}`,
    );
  },

  async drawLotteryPrize(formId: string, prizeId: string, count?: number) {
    return fetcher.post<FormLotteryDrawResultDTO>(
      `${config.apiUrl}/forms/${formId}/lottery/prizes/${prizeId}/draw`,
      count ? { count } : {},
    );
  },

  async listLotteryEntries(formId: string, query: { limit: number; offset: number; search?: string }) {
    const url = new URL(`${config.apiUrl}/forms/${formId}/lottery/entries`);
    url.searchParams.set("limit", String(query.limit));
    url.searchParams.set("offset", String(query.offset));
    if (query.search) url.searchParams.set("search", query.search);
    return fetcher.get<FormLotteryEntriesDTO>(url.toString());
  },

  async redeemLotteryEntry(formId: string, entryId: string) {
    return fetcher.post<{ alreadyRedeemed: boolean; entryId: string; redeemedAt: string | null }>(
      `${config.apiUrl}/forms/${formId}/lottery/entries/${entryId}/redeem`,
      {},
    );
  },
};

export const formCategoriesApi = {
  async list(
    query: FormCategoriesListQueryDTO,
  ): Promise<OffsetPageDTO<FormCategoryDTO>> {
    const url = new URL(`${config.apiUrl}/forms/categories`);
    url.searchParams.set("limit", String(query.limit));
    url.searchParams.set("offset", String(query.offset));
    if (query.status) url.searchParams.set("status", query.status);
    return fetcher.get<OffsetPageDTO<FormCategoryDTO>>(url.toString());
  },

  async lookup(args?: {
    q?: string;
    limit?: number;
    skip?: number;
  }): Promise<Array<{ id: string; name: string }>> {
    const url = new URL(`${config.apiUrl}/forms/categories/lookup`);
    url.searchParams.set("limit", String(args?.limit ?? 100));
    url.searchParams.set("skip", String(args?.skip ?? 0));
    if (args?.q?.trim()) url.searchParams.set("q", args.q.trim());
    const res = await fetcher.get<{
      data: Array<{ id: string; name: string }>;
    }>(url.toString());
    return res.data;
  },

  async create(body: {
    name: string;
    description?: string | null;
    status?: "active" | "inactive";
    order?: number;
  }): Promise<FormCategoryDTO> {
    return fetcher.post<FormCategoryDTO>(
      `${config.apiUrl}/forms/categories`,
      body,
    );
  },

  async patch(
    id: string,
    body: {
      name?: string;
      description?: string | null;
      status?: "active" | "inactive";
      order?: number | null;
    },
  ): Promise<FormCategoryDTO> {
    return fetcher.patch<FormCategoryDTO>(
      `${config.apiUrl}/forms/categories/${id}`,
      body,
    );
  },

  async remove(id: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${config.apiUrl}/forms/categories/${id}`,
    );
  },
};

/** ສຳລັບໃຊ້ກັບ FormResponsesPanel ໃນໜ້າ admin */
export const adminFormResponsesApi: FormResponsesApi = {
  listResponses: (formId, args) => formsApi.listResponses(formId, args),
  getResponsesSummary: (formId) => formsApi.getResponsesSummary(formId),
  fetchResponsesPrintData: (formId, options) =>
    formsApi.fetchResponsesPrintData(formId, options),
  downloadResponsesCsv: (formId, options) =>
    formsApi.downloadResponsesCsv(formId, options),
  getAnswerFileReadUrl: (formId, key) =>
    formsApi.getAnswerFileReadUrl(formId, key),
};
